import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPrint, FaSync, FaEye, FaDownload, FaCheck, FaCircle, FaInbox, FaExclamationTriangle, FaCog, FaKey, FaTrash, FaSearch, FaChevronDown, FaChevronRight, FaTimes, FaPen } from 'react-icons/fa';
import axios from 'axios';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';

const { API_BASE_URL } = config;
// Fallback do endereço da ponte enquanto a empresa não configurou no Nexus.
const PONTE_PADRAO = 'http://localhost:53123';
// Mesmo limite da ponte (DivisorPdf): acima disso o upload do DETRAN recusa.
const LIMITE_DETRAN = 4990000;
// Quantas linhas mostrar por vez em Digitalizados; "Mostrar mais" soma outro tanto.
const PAGINA = 50;
// Placa antiga (ABC1234) ou Mercosul (ABC1D23) — mesma regra da ponte.
const FORMATO_PLACA = /^[A-Z]{3}[0-9][A-J0-9][0-9]{2}$/;

function lerSecoesAbertas() {
  try {
    return { pendentes: true, processados: true, ...JSON.parse(localStorage.getItem('digitalizacaoSecoes') || '{}') };
  } catch { return { pendentes: true, processados: true }; }
}

// "abc-1d23" -> "ABC1D23" (para comparar e salvar sem traço/espaço).
const limparPlaca = (p) => String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

function tipoUsuarioDoToken() {
  try {
    const t = localStorage.getItem('jwtToken');
    return t ? JSON.parse(atob(t.split('.')[1])).tipoUsuario : null;
  } catch { return null; }
}

function Digitalizacao() {
  const { addToast } = useToast();
  const podeConfigurar = ['MANAGER', 'GERENTE'].includes(tipoUsuarioDoToken());

  const [ponteUrl, setPonteUrl] = useState(() => localStorage.getItem('ponteUrl') || PONTE_PADRAO);
  const [status, setStatus] = useState('conectando'); // conectando | online | offline
  const [processados, setProcessados] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [numeros, setNumeros] = useState({});
  const [salvando, setSalvando] = useState('');

  // Administração das listas: busca por número/placa, seções recolhíveis, paginação e miniatura ampliada.
  const [busca, setBusca] = useState('');
  const [abertas, setAbertas] = useState(lerSecoesAbertas);
  const [limiteProc, setLimiteProc] = useState(PAGINA);
  const [ampliada, setAmpliada] = useState('');
  // Correção da placa lida por OCR: { numero, valor } do documento em edição.
  const [editandoPlaca, setEditandoPlaca] = useState(null);
  const alternarSecao = (id) => setAbertas((a) => {
    const n = { ...a, [id]: !a[id] };
    try { localStorage.setItem('digitalizacaoSecoes', JSON.stringify(n)); } catch { /* sem storage: só não lembra */ }
    return n;
  });
  useEffect(() => { setLimiteProc(PAGINA); }, [busca]);

  // Config (vem do Nexus, por empresa)
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [enderecoConfig, setEnderecoConfig] = useState('');
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [token, setToken] = useState('');

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }), []);

  // 1) Ao abrir, pega o endereço da ponte configurado no Nexus (por empresa).
  useEffect(() => {
    axios.get(`${API_BASE_URL}/empresas/scan-config`, authHeader())
      .then(({ data }) => {
        if (data && data.enderecoPonte) {
          const url = data.enderecoPonte.replace(/\/+$/, '');
          setPonteUrl(url);
          setEnderecoConfig(url);
          localStorage.setItem('ponteUrl', url);
        } else {
          setEnderecoConfig(localStorage.getItem('ponteUrl') || PONTE_PADRAO);
        }
      })
      .catch(() => setEnderecoConfig(localStorage.getItem('ponteUrl') || PONTE_PADRAO));
  }, [authHeader]);

  // 2) Fala com a ponte (status + lista), e repete a cada 2s (documento novo aparece rápido).
  // Uma consulta por vez (se a anterior ainda não voltou, pula a vez) e só mostra "não encontrada"
  // depois de 3 falhas seguidas: uma resposta lenta não pode piscar a ponte como desconectada.
  const consultando = useRef(false);
  const falhas = useRef(0);
  const carregar = useCallback(async () => {
    if (consultando.current) return;
    consultando.current = true;
    try {
      const { data } = await axios.get(`${ponteUrl}/documentos`, { ...authHeader(), timeout: 10000 });
      falhas.current = 0;
      setStatus('online');
      setProcessados(data.processados || []);
      setPendentes(data.pendentes || []);
    } catch {
      falhas.current += 1;
      if (falhas.current >= 3) setStatus('offline');
    } finally {
      consultando.current = false;
    }
  }, [ponteUrl, authHeader]);

  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  useEffect(() => {
    falhas.current = 0;
    carregarRef.current();
    const t = setInterval(() => carregarRef.current(), 2000);
    return () => clearInterval(t);
  }, [ponteUrl]);

  const verArquivo = async (pasta, nome) => {
    try {
      const url = `${ponteUrl}/arquivo?pasta=${pasta}&nome=${encodeURIComponent(nome)}`;
      const { data } = await axios.get(url, { ...authHeader(), responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank');
    } catch {
      addToast('error', 'Não consegui abrir o arquivo na ponte.');
    }
  };

  const baixarArquivo = async (pasta, nome) => {
    try {
      const url = `${ponteUrl}/arquivo?pasta=${pasta}&nome=${encodeURIComponent(nome)}`;
      const { data } = await axios.get(url, { ...authHeader(), responseType: 'blob' });
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = nome.split('/').pop(); // parte de documento dividido vem como "pasta/arquivo"
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      addToast('error', 'Não consegui baixar o arquivo na ponte.');
    }
  };

  // Miniaturas da 1ª página dos pendentes (blob autenticado -> object URL), para o operador
  // reconhecer o documento pelo conteúdo e não pelo nome do arquivo.
  const [miniaturas, setMiniaturas] = useState({});
  const miniaturasRef = useRef({});
  const buscandoMini = useRef(new Set());
  useEffect(() => {
    const atuais = new Set(pendentes.map((d) => d.arquivo));
    // Libera as miniaturas de quem já saiu da lista.
    const sobra = {};
    Object.entries(miniaturasRef.current).forEach(([arq, url]) => {
      if (atuais.has(arq)) sobra[arq] = url; else URL.revokeObjectURL(url);
    });
    if (Object.keys(sobra).length !== Object.keys(miniaturasRef.current).length) {
      miniaturasRef.current = sobra;
      setMiniaturas(sobra);
    }
    pendentes.forEach((d) => {
      if (miniaturasRef.current[d.arquivo] || buscandoMini.current.has(d.arquivo)) return;
      buscandoMini.current.add(d.arquivo);
      axios.get(`${ponteUrl}/miniatura?pasta=pendentes&nome=${encodeURIComponent(d.arquivo)}&lado=360`,
        { ...authHeader(), responseType: 'blob', timeout: 15000 })
        .then(({ data }) => {
          const url = URL.createObjectURL(data);
          miniaturasRef.current = { ...miniaturasRef.current, [d.arquivo]: url };
          setMiniaturas(miniaturasRef.current);
        })
        .catch(() => { /* sem miniatura: o card mostra só o nome */ })
        .finally(() => buscandoMini.current.delete(d.arquivo));
    });
  }, [pendentes, ponteUrl, authHeader]);
  useEffect(() => () => Object.values(miniaturasRef.current).forEach((u) => URL.revokeObjectURL(u)), []);

  // Foco automático: quando chega um pendente novo, o cursor vai pro campo dele (se o operador
  // não estiver digitando em outro). Depois de salvar, vai pro próximo da lista.
  const inputsRef = useRef({});
  const vistos = useRef(null);
  const focarProximo = useRef(false);
  useEffect(() => {
    const nomes = pendentes.map((d) => d.arquivo);
    const primeiraCarga = vistos.current === null;
    const novos = primeiraCarga ? [] : nomes.filter((n) => !vistos.current.has(n));
    vistos.current = new Set(nomes);
    const ativo = document.activeElement;
    const digitando = ativo && ativo.tagName === 'INPUT' && ativo.value;
    let alvo = null;
    if (novos.length) alvo = novos[0]; // lista vem do mais recente p/ o mais antigo
    else if (focarProximo.current || primeiraCarga) alvo = nomes[0];
    focarProximo.current = false;
    if (alvo && !digitando && inputsRef.current[alvo]) inputsRef.current[alvo].focus();
  }, [pendentes]);

  const resolverPendente = async (arquivo) => {
    const numero = (numeros[arquivo] || '').replace(/\D/g, '');
    if (!numero) { addToast('warning', 'Digite o número antes de salvar.'); return; }
    setSalvando(arquivo);
    try {
      const { data } = await axios.post(`${ponteUrl}/pendentes/resolver`, { arquivo, numero }, authHeader());
      const partes = (data && data.partes && data.partes.length) || 1;
      addToast('success', partes > 1
        ? `Documento salvo como ${numero}, dividido em ${partes} partes (passou de 4,99 MB).`
        : `Documento salvo como ${numero}.`);
      setNumeros((n) => { const c = { ...n }; delete c[arquivo]; return c; });
      focarProximo.current = true;
      carregar();
    } catch {
      addToast('error', 'Não consegui salvar. Confira o número e a conexão com a ponte.');
    } finally {
      setSalvando('');
    }
  };

  // Exclui (a ponte move para a lixeira, recuperável por 30 dias na máquina da ponte).
  const excluirArquivo = async (pasta, arquivo, rotulo) => {
    if (!window.confirm(`Excluir ${rotulo}?\n\nO arquivo vai para a lixeira da ponte (fica guardado por 30 dias).`)) return;
    try {
      await axios.post(`${ponteUrl}/excluir`, { pasta, arquivo }, authHeader());
      addToast('success', 'Documento excluído.');
      if (pasta === 'pendentes') focarProximo.current = true;
      carregar();
    } catch {
      addToast('error', 'Não consegui excluir na ponte.');
    }
  };

  // Salva a placa corrigida na ponte (vale para todas as partes do documento). Vazio = sem placa.
  const salvarPlaca = async () => {
    if (!editandoPlaca) return;
    const { numero } = editandoPlaca;
    const placa = limparPlaca(editandoPlaca.valor);
    if (placa && !FORMATO_PLACA.test(placa)) { addToast('warning', 'Placa inválida. Use ABC1234 ou ABC1D23.'); return; }
    try {
      await axios.post(`${ponteUrl}/placa`, { numero, placa }, authHeader());
      setProcessados((lista) => lista.map((d) => (d.numero === numero ? { ...d, placa } : d)));
      setEditandoPlaca(null);
    } catch {
      addToast('error', 'Não consegui salvar a placa na ponte.');
    }
  };

  const salvarEndereco = async () => {
    const limpo = enderecoConfig.trim().replace(/\/+$/, '');
    setSalvandoConfig(true);
    try {
      await axios.post(`${API_BASE_URL}/empresas/scan-config`, { enderecoPonte: limpo, ativo: true }, authHeader());
      setPonteUrl(limpo);
      localStorage.setItem('ponteUrl', limpo);
      setStatus('conectando');
      addToast('success', 'Endereço da ponte salvo para a empresa.');
    } catch {
      addToast('error', 'Não consegui salvar (precisa ser gerente/manager).');
    } finally {
      setSalvandoConfig(false);
    }
  };

  const gerarToken = async () => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}/empresas/scan-config/gerar-token`, {}, authHeader());
      setToken(data.tokenPonte || '');
      addToast('success', 'Token gerado. Copie e configure na ponte (ele só aparece agora).');
    } catch {
      addToast('error', 'Não consegui gerar o token (precisa ser gerente/manager).');
    }
  };

  // Busca por número (ou parte do nome do arquivo) ou por placa (com ou sem traço) em Digitalizados.
  const termo = busca.trim().toLowerCase();
  const termoPlaca = limparPlaca(busca);
  const casa = (...campos) => !termo || campos.some((c) => String(c || '').toLowerCase().includes(termo));
  const procFiltrados = processados.filter((d) => casa(d.numero, d.arquivo)
    || (termoPlaca && limparPlaca(d.placa).includes(termoPlaca)));

  const cabecalhoSecao = (id, icone, texto) => (
    <h2 style={{ ...s.secaoTitulo, cursor: 'pointer' }} onClick={() => alternarSecao(id)} title={abertas[id] ? 'Minimizar' : 'Expandir'}>
      {abertas[id] ? <FaChevronDown style={s.chevron} /> : <FaChevronRight style={s.chevron} />} {icone} {texto}
    </h2>
  );

  const corStatus = status === 'online' ? '#27ae60' : status === 'offline' ? '#e74c3c' : '#f39c12';
  const textoStatus = status === 'online' ? 'Ponte conectada' : status === 'offline' ? 'Ponte não encontrada' : 'Conectando...';

  return (
    <div>
      <Header />
      <div style={s.container}>
        <div style={s.tituloLinha}>
          <h1 style={s.titulo}><FaPrint /> Digitalização</h1>
          <div style={s.statusBox}>
            <FaCircle style={{ color: corStatus, fontSize: 10 }} /> <span>{textoStatus}</span>
            <code style={s.ponteUrl}>{ponteUrl}</code>
            <button onClick={carregar} style={s.btnIcon} title="Atualizar"><FaSync /></button>
            {podeConfigurar && (
              <button onClick={() => setMostrarConfig((v) => !v)} style={s.btnIcon} title="Configurar"><FaCog /></button>
            )}
          </div>
        </div>

        {/* Config por empresa (gerente/manager) */}
        {podeConfigurar && mostrarConfig && (
          <div style={s.configBox}>
            <div style={s.configLinha}>
              <label style={s.configLabel}>Endereço da ponte desta empresa:</label>
              <input value={enderecoConfig} onChange={(e) => setEnderecoConfig(e.target.value)}
                     placeholder="http://IP-da-ponte:53123" style={s.inputUrl} />
              <button onClick={salvarEndereco} disabled={salvandoConfig} style={s.btnPrimary}>Salvar</button>
            </div>
            <div style={s.configLinha}>
              <button onClick={gerarToken} style={s.btnSecundario}><FaKey /> Gerar token de ativação da ponte</button>
              {token && (
                <code style={s.token} title="Copie e configure na ponte">{token}</code>
              )}
            </div>
            <small style={s.dicaConfig}>
              O endereço é onde as estações alcançam a ponte na rede (ex.: http://10.30.128.175:53123).
              O token identifica a ponte desta empresa — ele só aparece no momento em que é gerado.
            </small>
          </div>
        )}

        {status === 'offline' && (
          <div style={s.avisoOffline}>
            <FaExclamationTriangle /> Não consegui falar com a ponte em <b>{ponteUrl}</b>. Verifique se o programa da
            ponte está rodando e se o endereço está certo{podeConfigurar ? ' (engrenagem acima para configurar).' : '.'}
          </div>
        )}

        {/* Pendentes */}
        <div style={s.secao}>
          {cabecalhoSecao('pendentes', <FaInbox />, `Aguardando número (${pendentes.length})`)}
          {!abertas.pendentes ? null : pendentes.length === 0 ? (
            <p style={s.vazio}>Nenhum documento aguardando. Quando um documento for digitalizado sem código de barras legível, ele aparece aqui para você digitar o número.</p>
          ) : (
            pendentes.map((d) => {
              const grande = ampliada === d.arquivo;
              return (
                <div key={d.arquivo} style={s.cardPendente}>
                  <div style={{ ...s.miniBox, width: grande ? 260 : 64, minHeight: grande ? 200 : 80 }}
                       onClick={() => setAmpliada(grande ? '' : d.arquivo)}
                       title={grande ? 'Clique para diminuir' : 'Clique para ampliar'}>
                    {miniaturas[d.arquivo]
                      ? <img src={miniaturas[d.arquivo]} alt="1ª página" style={s.miniImg} />
                      : <span style={s.miniCarregando}>...</span>}
                  </div>
                  <div style={s.pendenteInfo}>
                    <b style={s.pendenteHora}>{formatarHora(d.modificado)}</b>
                    <span style={s.arqNome} title={d.arquivo}>{d.arquivo}</span>
                  </div>
                  <div style={s.acoes}>
                    <input ref={(el) => { inputsRef.current[d.arquivo] = el; }}
                      placeholder="nº do atendimento/processo" value={numeros[d.arquivo] || ''} inputMode="numeric"
                      onChange={(e) => setNumeros((n) => ({ ...n, [d.arquivo]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && resolverPendente(d.arquivo)} style={s.inputNumero} />
                    <button onClick={() => resolverPendente(d.arquivo)} disabled={salvando === d.arquivo} style={s.btnPrimary}>
                      <FaCheck /> {salvando === d.arquivo ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button onClick={() => verArquivo('pendentes', d.arquivo)} style={s.btnIconeAcao} title="Ver documento inteiro"><FaEye /></button>
                    <button onClick={() => baixarArquivo('pendentes', d.arquivo)} style={s.btnIconeAcao} title="Baixar"><FaDownload /></button>
                    <button onClick={() => excluirArquivo('pendentes', d.arquivo, 'este documento sem número')} style={s.btnExcluir} title="Excluir"><FaTrash /></button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Busca por número ou placa (Digitalizados) */}
        <div style={s.buscaBox}>
          <FaSearch style={{ color: '#888' }} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por número (atendimento ou processo) ou placa..."
                 style={s.inputBusca} />
          {busca && <button onClick={() => setBusca('')} style={s.btnIcon} title="Limpar busca"><FaTimes /></button>}
        </div>

        {/* Digitalizados */}
        <div style={s.secao}>
          {cabecalhoSecao('processados', <FaPrint />, `Digitalizados (${termo ? `${procFiltrados.length} de ${processados.length}` : processados.length})`)}
          {!abertas.processados ? null : procFiltrados.length === 0 ? (
            <p style={s.vazio}>{termo ? `Nenhum digitalizado com "${busca}".` : 'Nenhum documento digitalizado ainda.'}</p>
          ) : (<>

            <table style={s.tabela}>
              <thead>
                <tr><th style={s.th}>Hora</th><th style={s.th}>Número</th><th style={s.th} title="Lida automaticamente da 1ª página — pode ter erro; clique para corrigir">Placa</th><th style={s.th}>Arquivo</th><th style={s.th}>Tamanho</th><th style={s.th}></th></tr>
              </thead>
              <tbody>
                {procFiltrados.slice(0, limiteProc).map((d) => (
                  <tr key={d.arquivo}>
                    <td style={s.td}>{formatarHora(d.modificado)}</td>
                    <td style={s.td}>
                      <b>{d.numero}</b>
                      {d.partes > 1 && <span style={s.parte} title="Passou de 4,99 MB e foi dividido para caber no upload do DETRAN">parte {d.parte} de {d.partes}</span>}
                      {d.tamanho > LIMITE_DETRAN && <span style={s.acimaLimite} title="Uma única folha já passa do limite e não tem como dividir. Digitalize de novo com resolução menor.">acima de 5 MB</span>}
                    </td>
                    <td style={s.td}>
                      {editandoPlaca && editandoPlaca.numero === d.numero ? (
                        <input autoFocus value={editandoPlaca.valor} maxLength={8} placeholder="ABC1D23"
                          onChange={(e) => setEditandoPlaca({ numero: d.numero, valor: e.target.value.toUpperCase() })}
                          onKeyDown={(e) => { if (e.key === 'Enter') salvarPlaca(); if (e.key === 'Escape') setEditandoPlaca(null); }}
                          onBlur={() => setEditandoPlaca(null)}
                          style={s.inputPlaca} />
                      ) : (
                        <span style={s.placa} onClick={() => setEditandoPlaca({ numero: d.numero, valor: d.placa || '' })}
                              title="Lida automaticamente da 1ª página — pode ter erro. Clique para corrigir (Enter salva, Esc cancela).">
                          {d.placa === null ? <span style={s.placaVazia}>lendo...</span>
                            : !d.placa ? <span style={s.placaVazia}>—</span>
                              : <b>{d.placa}</b>}
                          <FaPen style={s.placaLapis} />
                        </span>
                      )}
                    </td>
                    <td style={s.td}>{d.arquivo}</td>
                    <td style={s.td}>{formatarTamanho(d.tamanho)}</td>
                    <td style={s.td}>
                      <div style={s.acoes}>
                        <button onClick={() => verArquivo('processados', d.arquivo)} style={s.btnVer}><FaEye /> Ver</button>
                        <button onClick={() => baixarArquivo('processados', d.arquivo)} style={s.btnBaixar}><FaDownload /> Baixar</button>
                        <button onClick={() => excluirArquivo('processados', d.arquivo,
                                  d.partes > 1 ? `a parte ${d.parte} de ${d.partes} do nº ${d.numero}` : `o documento nº ${d.numero}`)}
                                style={s.btnExcluir} title="Excluir"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {procFiltrados.length > limiteProc && (
              <button onClick={() => setLimiteProc((l) => l + PAGINA)} style={s.btnMais}>
                Mostrar mais ({procFiltrados.length - limiteProc} restantes)
              </button>
            )}
          </>)}
        </div>

      </div>
    </div>
  );
}

// Hora da digitalização (epoch ms da ponte). Só a hora se for hoje; senão data + hora.
function formatarHora(ms) {
  if (!ms) return '-';
  const d = new Date(ms);
  const hoje = new Date().toDateString() === d.toDateString();
  return hoje
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatarTamanho(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const s = {
  container: { maxWidth: 980, margin: '0 auto', padding: '20px 16px' },
  tituloLinha: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  titulo: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 24, color: '#4b2a8a', margin: 0 },
  statusBox: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#444', flexWrap: 'wrap' },
  ponteUrl: { background: '#f3f0fa', padding: '3px 8px', borderRadius: 4, color: '#4b2a8a', fontSize: 12 },
  btnIcon: { border: 'none', background: '#eee', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' },
  configBox: { background: '#f8f5fd', border: '1px solid #d9ccf0', borderRadius: 8, padding: '12px 14px', margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 },
  configLinha: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  configLabel: { fontSize: 13, color: '#555' },
  inputUrl: { flex: 1, minWidth: 220, padding: '7px 10px', border: '1px solid #ccc', borderRadius: 6 },
  token: { background: '#fff8e1', border: '1px solid #f0c36d', padding: '5px 10px', borderRadius: 6, fontSize: 13, wordBreak: 'break-all' },
  dicaConfig: { fontSize: 12, color: '#777' },
  avisoOffline: { background: '#fdecea', border: '1px solid #f5c6cb', color: '#a33', padding: '10px 12px', borderRadius: 8, display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' },
  secao: { marginTop: 22 },
  secaoTitulo: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, color: '#333', borderBottom: '2px solid #eee', paddingBottom: 6 },
  vazio: { color: '#888', fontSize: 14 },
  cardPendente: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#fff8e1', border: '1px solid #f0c36d', borderRadius: 8, padding: '6px 10px', marginTop: 6 },
  miniBox: { background: '#fff', border: '1px solid #e0d3a8', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in', overflow: 'hidden', flexShrink: 0, transition: 'width .15s' },
  miniImg: { width: '100%', display: 'block' },
  miniCarregando: { fontSize: 12, color: '#999' },
  pendenteInfo: { flex: '1 1 160px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  pendenteHora: { fontSize: 15, color: '#4b2a8a' },
  arqNome: { fontSize: 12, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  inputNumero: { padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, width: 210, fontSize: 16 },
  btnIconeAcao: { border: '1px solid #ddd', background: '#fff', color: '#555', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' },
  btnExcluir: { border: '1px solid #f5c6cb', background: '#fff', color: '#c0392b', borderRadius: 6, padding: '7px 10px', cursor: 'pointer' },
  chevron: { fontSize: 12, color: '#888' },
  buscaBox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 22, background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '6px 10px' },
  inputBusca: { flex: 1, border: 'none', outline: 'none', fontSize: 15, padding: '4px 0' },
  btnMais: { marginTop: 10, background: '#f3f0fa', color: '#4b2a8a', border: '1px solid #d9ccf0', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 },
  btnVer: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  btnBaixar: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  parte: { marginLeft: 8, fontSize: 11, background: '#eadcff', color: '#4b2a8a', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' },
  acimaLimite: { marginLeft: 8, fontSize: 11, background: '#fdecea', color: '#a33', border: '1px solid #f5c6cb', borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' },
  placa: { display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontFamily: 'monospace', fontSize: 14, whiteSpace: 'nowrap' },
  placaVazia: { color: '#aaa', fontFamily: 'inherit' },
  placaLapis: { fontSize: 10, color: '#bbb' },
  inputPlaca: { width: 96, padding: '4px 6px', border: '1px solid #6f42c1', borderRadius: 4, fontFamily: 'monospace', fontSize: 14 },
  acoes: { display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 },
  btnSecundario: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eadcff', color: '#4b2a8a', border: '1px solid #cdb6f0', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  tabela: { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#777', textTransform: 'uppercase', borderBottom: '1px solid #eee' },
  td: { padding: '8px 10px', fontSize: 14, borderBottom: '1px solid #f2f2f2' },
};

export default Digitalizacao;
